/**
 * BuyboxesListPage - Unified buybox list page with role-aware views
 *
 * Route: /buybox
 *
 * URL State:
 * - status: "active" | "inactive" (default: all, not in URL)
 * - search: string (default: empty, not in URL)
 *
 * Role-based behavior:
 * - Investor: View their buyboxes with edit/delete actions + Create button
 * - Agent: Unauthorized view (buyboxes are confidential investor data)
 * - Admin: View all buyboxes (read-only)
 */

import { useState, useCallback, useMemo } from 'react'
import { useNavigate, Link, useSearch } from '@tanstack/react-router'

import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { getErrorMessage } from '@/common/utils/errors'
import { DataGrid } from '@/common/components/data-grid'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { sprinkles } from '@/core/styles/sprinkles.css'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { AlertDialog } from '@/common/components/alert-dialog'
import {
  useBuyboxesSource,
  deleteBuybox,
  BuyboxesToolbar,
  BuyboxCard,
  AutoOfferDialog,
  type BuyboxResponse,
  type BuyboxFilters,
} from '@/features/buyboxes'
import * as styles from './index.css'

export const Route = createFileRoute({
  component: BuyboxesListPage,
})

// =============================================================================
// URL State Helpers
// =============================================================================

type StatusValue = 'active' | 'inactive'

function parseUrlStatus(value: string | null): StatusValue {
  if (value === 'inactive') return 'inactive'
  return 'active' // Default to active
}

function statusToIsActive(status: StatusValue): boolean {
  return status === 'active'
}

// =============================================================================
// Main Component
// =============================================================================

function BuyboxesListPage() {
  const { roles, token } = useAuth()
  const { showSuccessToast, showErrorToast } = useToast()
  const navigate = useNavigate()
  const source = useBuyboxesSource()
  const search = useSearch({ strict: false }) as { status?: string; search?: string }
  const [refreshKey, setRefreshKey] = useState(0)

  // Parse URL state
  const urlStatus = parseUrlStatus(search.status ?? null)
  const urlSearch = search.search ?? ''

  // Convert URL state to filters
  const filters = useMemo<BuyboxFilters>(
    () => ({ isActive: statusToIsActive(urlStatus) }),
    [urlStatus]
  )

  // Update URL params helper
  const updateUrl = useCallback(
    (updates: { status?: StatusValue; search?: string }) => {
      navigate({
        search: ((prev: Record<string, string | undefined>) => {
          const next = { ...prev }
          // Status: only set if not default ("active")
          if (updates.status !== undefined) {
            if (updates.status === 'active') {
              delete next.status
            } else {
              next.status = updates.status
            }
          }
          // Search: only set if not empty
          if (updates.search !== undefined) {
            if (updates.search === '') {
              delete next.search
            } else {
              next.search = updates.search
            }
          }
          return next
        }) as never,
        replace: true,
      })
    },
    [navigate]
  )

  // Handle filter change from toolbar
  const handleFiltersChange = useCallback(
    (newFilters: BuyboxFilters) => {
      const newStatus: StatusValue = newFilters.isActive ? 'active' : 'inactive'
      updateUrl({ status: newStatus })
    },
    [updateUrl]
  )

  // Handle search change from toolbar
  const handleSearchChange = useCallback(
    (value: string) => {
      updateUrl({ search: value })
    },
    [updateUrl]
  )

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<BuyboxResponse | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Auto-offer dialog state
  const [autoOfferTarget, setAutoOfferTarget] = useState<BuyboxResponse | null>(null)

  // Determine user role for view customization
  const isAgent = roles.includes('agent')
  const isInvestor = roles.includes('investor')
  const isAdmin = roles.includes('admin')

  // Refresh data after action
  const refreshData = useCallback(() => {
    setRefreshKey((k) => k + 1)
  }, [])

  // Handle edit buybox
  const handleEdit = useCallback(
    (buybox: BuyboxResponse) => {
      navigate({ to: '/buybox/$id/edit', params: { id: buybox.id } })
    },
    [navigate]
  )

  // Handle delete buybox - open confirmation
  const handleDeleteClick = useCallback((buybox: BuyboxResponse) => {
    setDeleteTarget(buybox)
  }, [])

  // Handle apply offers - open auto-offer dialog
  const handleApplyOffers = useCallback((buybox: BuyboxResponse) => {
    setAutoOfferTarget(buybox)
  }, [])

  // Handle confirmed delete
  const handleConfirmDelete = useCallback(async () => {
    if (!token || !deleteTarget) return

    setIsDeleting(true)
    try {
      await deleteBuybox(token, deleteTarget.id)
      showSuccessToast(`"${deleteTarget.name}" has been deleted.`)
      setDeleteTarget(null)
      refreshData()
    } catch (err) {
      showErrorToast(getErrorMessage(err) || 'Failed to delete buybox')
    } finally {
      setIsDeleting(false)
    }
  }, [token, deleteTarget, showSuccessToast, showErrorToast, refreshData])

  // Agent unauthorized view
  if (isAgent && !isAdmin) {
    return (
      <Stack gap="24" style={{ padding: '24px', height: '100%' }}>
        <Stack gap="4">
          <Text as="h1" variant="h4" weight="bold">
            Buyboxes
          </Text>
          <Text variant="bodySm" color="muted">
            Investor property search criteria
          </Text>
        </Stack>
        <Stack gap="16" align="center" justify="center" style={{ flex: 1 }}>
          <Icon name="lock" size={48} style={{ color: '#999' }} />
          <Text as="h2" variant="bodyLg" weight="bold">
            Access Restricted
          </Text>
          <Text variant="body" color="muted" style={{ textAlign: 'center', maxWidth: 400 }}>
            Buybox configurations contain confidential investor purchase criteria.
            This information is not available to agents.
          </Text>
          <Link to="/properties">
            <Button>Go to Properties</Button>
          </Link>
        </Stack>
      </Stack>
    )
  }

  // Page title and description based on role
  const pageTitle = isAdmin ? 'All Buyboxes' : 'My Buyboxes'
  const pageDescription = isAdmin
    ? 'View all investor buybox configurations.'
    : 'Manage your property search criteria.'

  return (
    <div className={styles.container}>
      <div className={styles.content}>
      {/* Page Header */}
      <div className={styles.header} data-tour-step="buybox-header">
        <Stack gap="4">
          <Text as="h1" variant="h4" weight="bold">
            {pageTitle}
          </Text>
          <Text
            variant="bodySm"
            color="muted"
            className={sprinkles({ display: { base: 'block', tabletDown: 'none' } })}
          >
            {pageDescription}
          </Text>
        </Stack>
        {isInvestor && (
          <Link to="/buybox/create" data-tour-step="buybox-create">
            <Button size="sm">
              <Icon name="add" size={16} />
              Create Buybox
            </Button>
          </Link>
        )}
      </div>

      {/* DataGrid */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          key={refreshKey}
          source={source}
          getItemKey={(b) => b.id}
          initialSort={{ field: 'name', direction: 'asc' }}
          initialFilters={filters as Record<string, unknown>}
          initialSearch={urlSearch}
          columns="auto-fill"
          minItemWidth="300px"
          gap="16"
          emptyMessage="No buyboxes found"
          renderItem={(buybox) => (
            <BuyboxCard
              buybox={buybox}
              readOnly={isAdmin && !isInvestor}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onApplyOffers={handleApplyOffers}
            />
          )}
        >
          {/* Toolbar with filters and search */}
          <DataGrid.Toolbar>
            <BuyboxesToolbar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              search={urlSearch}
              onSearchChange={handleSearchChange}
            />
          </DataGrid.Toolbar>
        </DataGrid>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>Delete buybox?</AlertDialog.Title>
          <AlertDialog.Description>
            Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel disabled={isDeleting}>Cancel</AlertDialog.Cancel>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>

      {/* Auto-offer dialog */}
      {autoOfferTarget && (
        <AutoOfferDialog
          open={!!autoOfferTarget}
          onOpenChange={(open) => !open && setAutoOfferTarget(null)}
          buyboxId={autoOfferTarget.id}
          buyboxName={autoOfferTarget.name}
          defaultPercentage={autoOfferTarget.auto_bid_percentage ?? 85}
        />
      )}
      </div>
    </div>
  )
}
